import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Sparkles, ChevronDown, ChevronUp, Edit2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { InfoTooltip } from '@/components/InfoTooltip';

export const MaterialSelector = ({ materials, selected, onSelect, onCostChange }) => {
  const [showDetails, setShowDetails] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [customCost, setCustomCost] = useState(null);
  const selectedMaterial = materials.find(m => m.id === selected) || materials[0];
  
  const displayCost = customCost !== null ? customCost : selectedMaterial?.cost_per_kg || 0;

  const handleCostUpdate = (newCost) => {
    setCustomCost(newCost);
    if (onCostChange) {
      onCostChange(newCost);
    }
  };

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 rounded-lg bg-blue-100">
              <Sparkles className="h-4 w-4 text-blue-600" />
            </div>
            <Label className="text-sm font-semibold text-gray-800 flex items-center">
              Material
              <InfoTooltip content="Choose your 3D printing filament type. Different materials have different densities and costs. This affects the final weight and material cost of your print." />
            </Label>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDetails(!showDetails)}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            {showDetails ? (
              <><ChevronUp className="h-3 w-3 mr-1" />Hide Details</>
            ) : (
              <><ChevronDown className="h-3 w-3 mr-1" />Details</>
            )}
          </Button>
        </div>
        
        <Select value={selected} onValueChange={(value) => {
          onSelect(value);
          setCustomCost(null); // Reset custom cost when material changes
          setEditingCost(false);
        }}>
          <SelectTrigger data-testid="material-selector" className="bg-gray-50 border-gray-300 hover:bg-gray-100 transition-all">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            {materials.map((material) => (
              <SelectItem 
                key={material.id} 
                value={material.id}
                className="hover:bg-gray-100 focus:bg-gray-100"
              >
                {material.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {showDetails && selectedMaterial && (
          <div className="mt-4 p-4 rounded-xl bg-gray-50 border border-gray-200 space-y-3 animate-in slide-in-from-top-2">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="p-2 rounded-lg bg-white shadow-sm">
                <span className="text-gray-600 block mb-1">Density</span>
                <span className="text-gray-900 font-mono font-semibold">{selectedMaterial.density} g/cm³</span>
              </div>
              <div className="p-2 rounded-lg bg-white shadow-sm">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-gray-600">Cost</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditingCost(!editingCost)}
                    className="h-5 w-5 p-0"
                  >
                    <Edit2 className="h-3 w-3 text-gray-500" />
                  </Button>
                </div>
                {editingCost ? (
                  <Input
                    type="number"
                    step="0.01"
                    value={displayCost}
                    onChange={(e) => handleCostUpdate(parseFloat(e.target.value) || 0)}
                    className="h-7 text-xs font-mono"
                    placeholder="$/kg"
                  />
                ) : (
                  <span className="text-purple-600 font-mono font-semibold">
                    ${displayCost}/kg
                    {customCost !== null && <span className="text-xs text-gray-500 ml-1">(custom)</span>}
                  </span>
                )}
              </div>
            </div>
            <p className="text-xs text-gray-600 leading-relaxed pt-2 border-t border-gray-200">
              {selectedMaterial.description}
            </p>
          </div>
        )}
      </div>
    </Card>
  );
};